import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
	Button,
	Divider,
	IconButton,
	ListItemButton,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import React, { useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate } from 'react-router-dom';
import {
	ADVANCED_SEARCH_LABEL_2,
	SEARCH_FEATURE_GUIDE,
} from '../../data/labelsAndMessages';
import KeyboardShortcut from '../KeyboardShortcut';
import { SEARCH_DOCS_URL } from '../search/constants';
import {
	SEARCH__BUTTON_OPEN_SEARCH_KEYBOARD_SHORTCUT,
	SEARCH__BUTTON_TEXT,
	SEARCH___INPUT_CLOSE,
	SEARCH___INPUT_CLOSE_KEYBOARD_SHORTCUT,
	SEARCH___INPUT_PLACEHOLDER,
} from './constants';
import { CoreSearchResultList } from './CoreSearchResultList';
import {
	modalStyles,
	searchButtonStyles,
	searchInputStyles,
	searchModalHelpStyles,
	searchResultsStyles,
	SEARCH_BOX__SEARCH_INPUT_HEIGHT,
} from './styles';
import useDebounce, { useArrowUpOrDown } from './util';

const CoreSearch = () => {
	const navigate = useNavigate();
	const searchInputRef = useRef(null);
	const searchResultRefs = useRef([]);
	const [focusedListItemIndex, setFocusedListItemIndex] = useState(-1);
	const [open, setOpen] = React.useState(false);
	const [searchQuery, setSearchQuery] = React.useState('');
	const debouncedSearch = useDebounce(searchQuery, 500);
	const searchRequest = { searchString: debouncedSearch };

	const handleOpen = () => {
		setOpen(true);
	};
	const handleClose = () => {
		setOpen(false);
	};

	const handleSearch = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		setSearchQuery(e.target.value);
	};

	const selectPrevListItem = (e: any) => {
		if (focusedListItemIndex > 0) {
			searchResultRefs.current[focusedListItemIndex - 1].focus();
			setFocusedListItemIndex(focusedListItemIndex - 1);
		}
		// Focusing on search bar
		if (focusedListItemIndex === 0) {
			searchInputRef.current.focus();
			setFocusedListItemIndex(-1);
		}
	};

	const selectNextListItem = (e: any) => {
		if (focusedListItemIndex < searchResultRefs.current.length - 1) {
			searchResultRefs.current[focusedListItemIndex + 1].focus();
			setFocusedListItemIndex(focusedListItemIndex + 1);
		}
	};

	useHotkeys('/', (e) => {
		e.preventDefault();
		handleOpen();
	});
	useHotkeys('esc', handleClose);
	useArrowUpOrDown(selectPrevListItem, selectNextListItem);

	return (
		<>
			<Tooltip
				title={
					<KeyboardShortcut
						title='Search'
						shortcut={[SEARCH__BUTTON_OPEN_SEARCH_KEYBOARD_SHORTCUT]}
					/>
				}
			>
				<Button
					onClick={handleOpen}
					sx={searchButtonStyles}
					id='Button__open-search'
				>
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<SearchIcon />
						<Typography sx={{ ml: 2, color: 'black.lighter' }}>
							{SEARCH__BUTTON_TEXT}
						</Typography>
					</Box>
				</Button>
			</Tooltip>
			<Modal open={open} onClose={handleClose}>
				<Box sx={modalStyles}>
					<Box
						display='flex'
						flexDirection='column'
						sx={{ height: SEARCH_BOX__SEARCH_INPUT_HEIGHT }}
					>
						<Box component='header' sx={searchInputStyles}>
							<TextField
								fullWidth
								autoFocus
								id='Search__search-input'
								name='search-input'
								placeholder={SEARCH___INPUT_PLACEHOLDER}
								variant='outlined'
								value={searchQuery}
								inputRef={searchInputRef}
								onChange={handleSearch}
								InputProps={{
									sx: { borderRadius: '28px' },
									startAdornment: <SearchIcon />,
									endAdornment: (
										<Tooltip
											placement='top-end'
											title={
												<KeyboardShortcut
													shortcut={[SEARCH___INPUT_CLOSE_KEYBOARD_SHORTCUT]}
													title={SEARCH___INPUT_CLOSE}
												/>
											}
										>
											<IconButton onClick={handleClose}>
												<CloseIcon />
											</IconButton>
										</Tooltip>
									),
								}}
								inputProps={{
									sx: { py: 1.5 },
									'data-testid': 'search-input',
								}}
								FormHelperTextProps={{ sx: { mr: 0, alignSelf: 'flex-end' } }}
								helperText={
									<Button
										onClick={() => {
											handleClose();
											navigate('/search');
										}}
										sx={{
											textDecoration: 'underline',
											typography: 'captionMedium',
										}}
									>
										{ADVANCED_SEARCH_LABEL_2()}
									</Button>
								}
							/>
						</Box>
					</Box>
					<Box sx={searchResultsStyles}>
						<CoreSearchResultList
							afterListClick={handleClose}
							searchRequest={searchRequest}
						/>
					</Box>
					<Divider />
					<ListItemButton
						sx={{ p: 0 }}
						onClick={() => {
							window.open(SEARCH_DOCS_URL, '_blank', 'noopener noreferrer');
						}}
						color='primary'
						data-testid='advanced-search'
					>
						<Box sx={searchModalHelpStyles} display='flex'>
							<DescriptionOutlinedIcon sx={{ mr: 1, color: 'black.main' }} />
							{SEARCH_FEATURE_GUIDE()}
						</Box>
					</ListItemButton>
				</Box>
			</Modal>
		</>
	);
};

export default CoreSearch;
